import './Login.scss';

import {
  Button,
  ButtonIconPosition,
  ButtonStyle,
  IconSize,
  Icons,
  InlineNotification,
  LoadingBar,
  LoadingButton,
  NotificationType,
  WarningBar,
  Welcome,
  colors,
} from '@pdcfrontendui/components';
import ModalWrapper, {
  ModalType,
  ModalWrapperProps,
} from '../Modal/ModalWrapper';
import React, { useEffect, useMemo } from 'react';
import {
  statusbarLightContent,
  updateStatusBar,
} from '@pdcshell/helpers/statusBar';
import { useCurrentLangCode, useLang } from '../hooks/useLang';

import { GetVersionModel } from '../api/model';
import LanguageSelectContainer from '../LanguageSelect/LanguageSelectContainer';
import LoginUsernamePasswordForm from './LoginUsernamePasswordForm';
import { LogonTypeEnum } from '@pdcplan/shared/enums';
import NoNetworkEmptyState from './NoNetworkEmptyState';
import NoServerEmptyState from './NoServerEmptyState';
import { convertColorVarToHex } from '@pdcfrontendui/components/colors';
import ids from '../testing/ids';
import { isCordovaApp } from '@pdcshell/helpers/utils';
import {
  openIDConfigFromGlobalSettings,
  type OpenIDConnectConfig,
} from '@pdcplan/shared/utils/OpenID';
import { GlobalSettings } from '@pdcplan/shared/api/Common';

export type LoginProps = Record<string, never>;

export type StateFromProps = {
  errorMessage: string;
  version: GetVersionModel | null;
  openModals: ModalWrapperProps[];
  modalLoading: boolean;
  loginType?: LogonTypeEnum;
  attemptingLogin: boolean;
  loadingGetSettings: boolean;
  loadingGetVersion: boolean;
  hasInternet: boolean;
  connectionError: Error | null;
  globalSettings: GlobalSettings;
};

export type DispatchFromProps = {
  attemptLogin: (username: string, password: string) => void;
  attemptOpenIdLogin: (settings: OpenIDConnectConfig) => Promise<void>;
  openModal: (modal: ModalWrapperProps) => void;
  closeModal: () => void;
  showErrorModal: (errorInfo?: {
    error?: Error | null;
    title?: string;
    isConnectionError?: boolean;
  }) => void;
};

export type LoginValues = {
  username: string;
  password: string;
};

const initialValues: LoginValues = {
  username: '',
  password: '',
};

type Props = StateFromProps & DispatchFromProps;

const Login = ({
  attemptLogin,
  attemptOpenIdLogin,
  loginType,
  errorMessage,
  openModals,
  openModal,
  closeModal,
  showErrorModal,
  version,
  modalLoading,
  attemptingLogin,
  loadingGetSettings,
  loadingGetVersion,
  hasInternet,
  connectionError,
  globalSettings,
}: Props) => {
  const loading = attemptingLogin || loadingGetSettings || loadingGetVersion;

  const isCordova = isCordovaApp();
  const lang = useLang();
  const currentLangCode = useCurrentLangCode();

  const openId = useMemo(
    () => openIDConfigFromGlobalSettings(globalSettings, true, !!errorMessage),
    [globalSettings, errorMessage]
  );

  // RATJ: this is a little hack for PDC-shell for IOS with big screens - html defines the color of webView background
  // And since our login screen is not white like the rest of the app, we add a class to set background color of html
  useEffect(() => {
    const html = document.documentElement;
    html.classList.add('isLoginScreen');
    if (isCordova) {
      if (hasInternet && !connectionError) {
        updateStatusBar(
          convertColorVarToHex(colors.colorNeutralLighter),
          false
        );
        setTimeout(() => {
          statusbarLightContent(false);
        }, 200);
      } else {
        updateStatusBar(convertColorVarToHex(colors.colorErrorLightest), false);
        setTimeout(() => {
          statusbarLightContent(false);
        }, 200);
      }
    }
    return () => {
      html.classList.remove('isLoginScreen');
      if (isCordovaApp()) {
        updateStatusBar(convertColorVarToHex(colors.colorPrimaryDarkest), true);
      }
    };
  }, [connectionError, hasInternet, isCordova]);

  const getLoginTypeEnumFromString = (
    loginTypeString: string
  ): LogonTypeEnum => {
    // The casing of loginTypeString may not match the casing of LogonTypeEnum.
    const loginTypeLowerCase = loginTypeString.toLowerCase();
    const value = Object.keys(LogonTypeEnum).find(
      (key) => key.toLowerCase() === loginTypeLowerCase
    );
    // Hack to get enum value from a string

    if (!value)
      throw new Error(`Login Type "${loginTypeLowerCase}" not found.`);

    return LogonTypeEnum[value as keyof typeof LogonTypeEnum];
  };

  const loginTypeEnum =
    typeof loginType === 'string'
      ? getLoginTypeEnumFromString(loginType)
      : loginType;

  const renderModal = () => {
    let modalContent: ModalWrapperProps;

    if (openModals.length > 0) {
      modalContent = openModals[openModals.length - 1] as ModalWrapperProps;
    } else {
      modalContent = {
        shouldShow: false,
        modalType: null,
      };
    }

    return (
      <ModalWrapper
        modalType={modalContent.modalType}
        modalBtn={modalContent.modalBtn}
        shouldShow={modalContent.shouldShow}
        content={modalContent.content}
        onClose={() => {
          closeModal();
        }}
        version={version}
        loading={modalLoading}
      />
    );
  };

  return (
    <div className="LoginNew">
      <div className="header">
        {(!hasInternet || connectionError) && (
          <WarningBar
            text={
              !hasInternet
                ? lang.NoNetwork
                : lang.YourAppIsHavingProblemsWithTheServer
            }
            buttonText={lang.MoveOn}
            onClick={() => {
              if (!hasInternet) {
                showErrorModal({
                  isConnectionError: true,
                  title: lang.logonBiometricAuthErrorTitle,
                });
              } else {
                showErrorModal({
                  error: connectionError,
                  title: lang.logonBiometricAuthErrorTitle,
                });
              }
            }}
          />
        )}
        <div className="headerWrapper">
          <div className="headerLeft">
            {window.pdc?.shell?.emit && (
              <Button
                variant={ButtonStyle.Ghost}
                icon={Icons.ChevronLeft}
                iconPosition={ButtonIconPosition.Left}
                onClick={() => {
                  window.pdc.shell.emit('show-picker');
                }}
                id={ids.Login.changeOrgButton}
              >
                {lang.ChangeOrganization}
              </Button>
            )}
          </div>
          <div className="headerRight">
            <div className="login-language-select">
              <LanguageSelectContainer languageCode={currentLangCode} />
            </div>
            <Icons.Info
              className="questionButton"
              onClick={() => {
                openModal({
                  modalType: ModalType.info,
                });
              }}
              size={IconSize.XSmall}
              id={ids.Login.InfoButton}
            />
          </div>
        </div>
      </div>
      <div className="content">
        <div className="errorMessages">
          {errorMessage && (
            <InlineNotification type={NotificationType.Error}>
              <div className="errorItem">{errorMessage}</div>
            </InlineNotification>
          )}
        </div>
        <div className="login-wrapper">
          <Welcome
            titleWelcome={lang.LoginTitleWelcome}
            appName={lang.TeamPlan}
          />
          <div className="login-box">
            {!hasInternet ? (
              <NoNetworkEmptyState />
            ) : connectionError ? (
              <NoServerEmptyState />
            ) : (
              <>
                {loadingGetSettings || loadingGetVersion ? (
                  <div className="loading">
                    <LoadingBar
                      progress={-1}
                      title={lang.LoginLoadingData}
                      description={lang.Loading}
                    />
                  </div>
                ) : loginTypeEnum === LogonTypeEnum.openId ? (
                  <LoadingButton
                    className="openIdButton"
                    id={ids.Login.loginOpenIdButton}
                    loading={loading}
                    onClick={() => {
                      attemptOpenIdLogin(openId);
                    }}
                  >
                    {lang.login}
                  </LoadingButton>
                ) : (
                  <LoginUsernamePasswordForm
                    initialValues={initialValues}
                    onSubmitUsernamePass={(values: LoginValues) => {
                      attemptLogin(values.username, values.password);
                    }}
                    showErrors={false}
                    loading={loading}
                    currentLanguage={lang}
                  />
                )}
              </>
            )}
          </div>
        </div>
        <div style={{ gridRow: 3, height: '16px' }}></div>
      </div>
      {renderModal()}
    </div>
  );
};

export default Login;
